import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Icon, Link, Loading, Checkbox, Popover2, Button, ButtonTooltip } from '@evertrue/et-components';
import EverTrue from 'app';
import { fetchUsersById } from 'apps/volunteers/vms-queries';
import useCadenceMembers from '../../hooks/use-cadence-members';
import useCadenceMutations from '../../hooks/use-cadence-mutations';
import ContactAddTo from 'apps/contact/components/contacts/table/contact-add-to';
import WaitCountdown from './wait-countdown';
import { STATUS_COLORS as STATUS_MAP } from '../../utils/member-status';
import { formatDateOrDash } from '../../utils/cadence-data-mappers';
import { getDisplayStatusLabel, getStatusTooltipTitle } from '../../utils/cadence-enums';

/**
 * Issue popover showing per-issue details.
 */
const IssuePopover = ({ issues }) => {
  if (!issues || issues.length === 0) return null;

  const severityIcon = (type) => {
    switch (type) {
      case 'error':
        return <span className="issue-severity-icon is-error">&times;</span>;
      case 'blocked':
        return <span className="issue-severity-icon is-blocked">&#9940;</span>;
      default:
        return <span className="issue-severity-icon is-warning">&#9888;</span>;
    }
  };

  return (
    <div className="recipient-issue-popover">
      {issues.map((issue, idx) => (
        <div key={idx} className="recipient-issue-popover--item">
          {severityIcon(issue.type)}
          <div className="recipient-issue-popover--detail">
            <span className="recipient-issue-popover--step">{issue.step}</span>
            <span className="recipient-issue-popover--message">{issue.message}</span>
            {issue.code && <code className="recipient-issue-popover--code">{issue.code}</code>}
          </div>
        </div>
      ))}
    </div>
  );
};

IssuePopover.propTypes = {
  issues: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      step: PropTypes.string,
      message: PropTypes.string,
      code: PropTypes.string,
    })
  ),
};

/**
 * Status badge with optional tooltip explanation.
 * Resolves "User #XXXX" patterns in statusExplanation to real user names.
 */
const DMARC_EXPLANATION =
  "We attempted to send this message, but it was not delivered because your org's DNS was not properly set up at send time.";

const StatusBadge = ({ member, cadenceStatus }) => {
  const isDmarcFailure = (member.removalReason || '').toLowerCase() === 'dmarc_policy';
  const statusStyle = isDmarcFailure
    ? STATUS_MAP['failed_send']
    : STATUS_MAP[member.displayStatus] || { bg: '#f3f4f6', color: '#6b7280' };
  const rawExplanation = isDmarcFailure ? DMARC_EXPLANATION : member.statusExplanation;
  const [explanation, setExplanation] = useState(rawExplanation);
  const tooltipTitle = getStatusTooltipTitle(member);

  useEffect(() => {
    if (!rawExplanation) return;
    const matches = [...rawExplanation.matchAll(/User #(\d+)/g)];
    if (!matches.length) {
      setExplanation(rawExplanation);
      return;
    }
    const ids = [...new Set(matches.map((m) => Number(m[1])))];
    fetchUsersById(ids)
      .then((users) => {
        let resolved = rawExplanation;
        (users || []).forEach((user) => {
          if (user?.name && user.name !== 'Removed User') {
            resolved = resolved.replace(new RegExp(`User #${user.id}`, 'g'), user.name);
          }
        });
        setExplanation(resolved);
      })
      .catch(() => setExplanation(rawExplanation));
  }, [rawExplanation]);

  // When the bulk message is actively sending, pending members haven't been processed yet.
  // Relabel from "Pending" to "Sending" so users understand the send is in flight.
  const isCadenceSending = (cadenceStatus || '').toLowerCase() === 'sending';
  const label = isDmarcFailure
    ? 'Failed Send'
    : isCadenceSending && member.displayStatus === 'pending'
      ? 'Sending'
      : getDisplayStatusLabel(member);

  const badge = (
    <span
      className={`cadence-recipients-table--status-badge ${explanation ? 'cadence-recipients-table--status-badge-hoverable' : ''}`}
      style={{ background: statusStyle.bg, color: statusStyle.color }}
    >
      {label}
      {explanation && <Icon icon="info" style={{ fontSize: 12, marginLeft: 4, cursor: 'pointer' }} />}
    </span>
  );

  if (explanation) {
    return (
      <ButtonTooltip contentWidth={280} trigger={badge}>
        <div className="cadence-recipients-table--status-tooltip">
          {tooltipTitle && <strong className="cadence-recipients-table--status-tooltip-title">{tooltipTitle}</strong>}
          <p className="cadence-recipients-table--status-tooltip-body">{explanation}</p>
        </div>
      </ButtonTooltip>
    );
  }

  return badge;
};

StatusBadge.propTypes = {
  member: PropTypes.object.isRequired,
  cadenceStatus: PropTypes.string,
};

/**
 * Recipients table driven by real cadence member data.
 * Fetches members from the cadence API, resolves contact names via fetchBasicContactInfo.
 */
/** Filter options for the full cadence view (Cadence Recipients table). */
const CADENCE_FILTERS = [
  { label: 'All Recipients', value: 'all' },
  { label: 'N/A', value: 'n_a' },
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Completed', value: 'completed' },
  { label: 'Early Exit: Manual', value: 'early_exit_manual' },
  { label: 'Early Exit: Skipped Send', value: 'early_exit_skipped' },
  { label: 'Early Exit: Failed Send', value: 'early_exit_failed_send' },
  { label: 'Early Exit: Configured Exit Event', value: 'early_exit_configured' },
  { label: 'Success Reached', value: 'success_reached' },
  { label: 'Success Not Reached', value: 'success_not_reached' },
];

/** Filter options when viewing a specific step's recipients (email steps). */
const STEP_EMAIL_FILTERS = [
  { label: 'All Recipients', value: 'all' },
  { label: 'Delivered', value: 'delivered' },
  { label: 'Opened', value: 'opened' },
  { label: 'Clicked', value: 'clicked' },
  { label: 'Replied', value: 'replied' },
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Completed', value: 'completed' },
  { label: 'Early Exit: Manual', value: 'early_exit_manual' },
  { label: 'Early Exit: Skipped Send', value: 'early_exit_skipped' },
  { label: 'Early Exit: Failed Send', value: 'early_exit_failed_send' },
  { label: 'Early Exit: Configured Exit Event', value: 'early_exit_configured' },
  { label: 'Unsubscribed', value: 'unsubscribed' },
  { label: 'Exited', value: 'exited' },
];

/** Filter options when viewing a specific manual step's recipients. */
const STEP_MANUAL_FILTERS = [
  { label: 'All Recipients', value: 'all' },
  { label: 'Task Completed', value: 'task_completed' },
  { label: 'Task Skipped', value: 'task_skipped' },
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Completed', value: 'completed' },
  { label: 'Early Exit: Manual', value: 'early_exit_manual' },
  { label: 'Early Exit: Skipped Send', value: 'early_exit_skipped' },
  { label: 'Early Exit: Failed Send', value: 'early_exit_failed_send' },
  { label: 'Early Exit: Configured Exit Event', value: 'early_exit_configured' },
  { label: 'Success Reached', value: 'success_reached' },
  { label: 'Exited', value: 'exited' },
];

const BULK_FILTERS = [
  { label: 'All Recipients', value: 'all' },
  { label: 'N/A', value: 'n_a' },
  { label: 'Send Scheduled', value: 'send_scheduled' },
  { label: 'Pending', value: 'pending' },
  { label: 'Skipped', value: 'skipped' },
  { label: 'Sent', value: 'sent' },
  { label: 'Delivered', value: 'delivered' },
  { label: 'Failed Send', value: 'failed_send' },
  { label: 'Opened', value: 'opened' },
  { label: 'Clicked', value: 'clicked' },
  { label: 'Replied', value: 'replied' },
  { label: 'Success Reached', value: 'success_reached' },
  { label: 'Unsubscribed', value: 'unsubscribed' },
];

// ============================================================================
// Column definitions per view mode (for Manage Columns)
// ============================================================================
const CADENCE_VIEW_COLUMNS = [
  { field: 'dateAdded', label: 'Date Added' },
  { field: 'status', label: 'Current Status' },
  { field: 'reachedGoal', label: 'Reached Goal' },
  { field: 'viewDetails', label: 'View Details', alwaysVisible: true },
];

const STEP_EMAIL_COLUMNS = [
  { field: 'delivered', label: 'Delivered' },
  { field: 'opened', label: 'Opened' },
  { field: 'clicked', label: 'Clicked' },
  { field: 'replied', label: 'Replied' },
  { field: 'unsubscribed', label: 'Unsubscribed' },
  { field: 'exited', label: 'Exited' },
  { field: 'viewDetails', label: 'View Details', alwaysVisible: true },
];

const STEP_MANUAL_COLUMNS = [
  { field: 'taskCompleted', label: 'Task Completed' },
  { field: 'taskSkipped', label: 'Task Skipped' },
  { field: 'successReached', label: 'Success Reached' },
  { field: 'exited', label: 'Exited' },
  { field: 'viewDetails', label: 'View Details', alwaysVisible: true },
];

const BULK_VIEW_COLUMNS = [
  { field: 'status', label: 'Status' },
  { field: 'sent', label: 'Sent' },
  { field: 'delivered', label: 'Delivered' },
  { field: 'opened', label: 'Opened' },
  { field: 'clicked', label: 'Clicked' },
  { field: 'replied', label: 'Replied' },
  { field: 'successReached', label: 'Success Reached' },
  { field: 'error', label: 'Error' },
  { field: 'unsubscribed', label: 'Unsubscribed' },
];

const RECIPIENTS_COL_STORAGE_KEY = 'cadence-recipients-columns';

const loadRecipientColumnVisibility = (mode) => {
  try {
    const stored = localStorage.getItem(`${RECIPIENTS_COL_STORAGE_KEY}-${mode}`);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

const saveRecipientColumnVisibility = (mode, visibility) => {
  try {
    localStorage.setItem(`${RECIPIENTS_COL_STORAGE_KEY}-${mode}`, JSON.stringify(visibility));
  } catch {
    // ignore storage errors
  }
};

const ManageRecipientColumnsPopover = ({ columns, visibility, onChange, onClose, anchorRef }) => {
  const ref = useRef(null);
  const [style, setStyle] = useState({});

  useEffect(() => {
    if (anchorRef?.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setStyle({
        position: 'fixed',
        top: rect.bottom + 4,
        right: window.innerWidth - rect.right,
        zIndex: 9999,
      });
      // If popover would go off-screen bottom, flip to above
      requestAnimationFrame(() => {
        if (ref.current) {
          const popRect = ref.current.getBoundingClientRect();
          if (popRect.bottom > window.innerHeight - 8) {
            setStyle((s) => ({ ...s, top: rect.top - popRect.height - 4 }));
          }
        }
      });
    }
  }, [anchorRef]);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  return (
    <div ref={ref} className="cadence-recipients-table--manage-cols-popover" style={style}>
      <div className="cadence-recipients-table--manage-cols-header">Manage Columns</div>
      {columns
        .filter((c) => !c.alwaysVisible)
        .map((col) => (
          <label key={col.field} className="cadence-recipients-table--manage-cols-row">
            <input
              type="checkbox"
              checked={visibility[col.field] !== false}
              onChange={(e) => onChange(col.field, e.target.checked)}
            />
            {col.label}
          </label>
        ))}
    </div>
  );
};

ManageRecipientColumnsPopover.propTypes = {
  columns: PropTypes.array.isRequired,
  visibility: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  anchorRef: PropTypes.object,
};

const CadenceRecipientsTable = ({
  cadenceId,
  cadenceStatus,
  onViewDetails,
  stepEventId,
  stepMemberIds,
  stepRecipientMap,
  steps,
  timeframeFilter,
  variant,
  title: titleProp,
  refreshKey,
  onMembersChanged,
}) => {
  const isBulk = variant === 'bulk';
  const isStepView = !!stepEventId;
  const selectedStep = isStepView && steps ? steps.find((s) => String(s.id) === String(stepEventId)) : null;
  const isManualStep = selectedStep?.type === 'task' || selectedStep?.isAutomated === false;
  const [searchQuery, setSearchQuery] = useState('');
  const [engagementFilter, setEngagementFilter] = useState('all');
  const [hasIssuesFilter, setHasIssuesFilter] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedContactIds, setSelectedContactIds] = useState(new Set());
  const [confirmBulkRemove, setConfirmBulkRemove] = useState(false);
  const [bulkRemoving, setBulkRemoving] = useState(false);
  const tableRef = useRef(null);
  const colsBtnRef = useRef(null);
  const [showManageCols, setShowManageCols] = useState(false);

  // Determine column mode and load visibility
  const columnMode = isBulk ? 'bulk' : isManualStep ? 'manual' : isStepView ? 'step' : 'cadence';
  const columnDefs = isBulk
    ? BULK_VIEW_COLUMNS
    : isManualStep
      ? STEP_MANUAL_COLUMNS
      : isStepView
        ? STEP_EMAIL_COLUMNS
        : CADENCE_VIEW_COLUMNS;
  const [colVisibility, setColVisibility] = useState(() => loadRecipientColumnVisibility(columnMode));
  const isColVisible = (field) => colVisibility[field] !== false;
  const toggleColVisibility = useCallback(
    (field, visible) => {
      setColVisibility((prev) => {
        const next = { ...prev, [field]: visible };
        saveRecipientColumnVisibility(columnMode, next);
        return next;
      });
    },
    [columnMode]
  );

  // Build server-side filter from engagement dropdown + step + timeframe.
  // useMemo gives this a stable object identity — only recomputes when filter
  // inputs change. This prevents the JSON.stringify dep in useCadenceMembers
  // from producing a new string on every render (the activityAfter date would
  // shift by milliseconds on each render, causing an infinite re-fetch loop).
  const serverFilter = useMemo(() => {
    const filter = {};

    // Step filtering — server-side via activityAtEventId (members who have
    // any activity at this step, not just members currently at the step).
    if (stepEventId) {
      filter.activityAtEventId = String(stepEventId);
    }

    // Timeframe filtering — converts UI timeframe to an ISO date for activityAfter.
    if (timeframeFilter && timeframeFilter !== 'all_time') {
      const daysMap = { '1_week': 7, '1_month': 30, '3_months': 90, '6_months': 180, '12_months': 365 };
      const days = daysMap[timeframeFilter];
      if (days) {
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - days);
        // Truncate to day boundary so the value is stable within the same session day.
        cutoff.setHours(0, 0, 0, 0);
        filter.activityAfter = cutoff.toISOString();
      }
    }

    if (engagementFilter !== 'all') {
      // Use decomposed boolean filters when available, fall back to recipientFilter enum
      const BOOLEAN_FILTER_MAP = {
        sent: { emailSent: true },
        delivered: { emailDelivered: true },
        opened: { emailOpened: true },
        clicked: { emailClicked: true },
        replied: { emailReplied: true },
        unsubscribed: { emailUnsubscribed: true },
        task_completed: { taskCompleted: true },
        task_skipped: { taskSkipped: true },
        success_reached: { isSuccessful: true },
        success_not_reached: { isSuccessful: false },
      };

      const ENUM_FILTER_MAP = {
        n_a: 'N_A',
        in_progress: 'IN_PROGRESS',
        completed: 'COMPLETED',
        early_exit_manual: 'EARLY_EXIT_MANUAL',
        early_exit_skipped: 'EARLY_EXIT_SKIPPED',
        early_exit_failed_send: 'EARLY_EXIT_FAILED_SEND',
        early_exit_configured: 'EARLY_EXIT_CONFIGURED',
        pending: 'PENDING',
        send_scheduled: 'PENDING',
        skipped: 'SKIPPED',
        failed_send: 'FAILED_SEND',
      };

      // "Exited" uses the status filter, not recipientFilter enum
      if (engagementFilter === 'exited') {
        filter.status = ['REMOVED'];
        return Object.keys(filter).length > 0 ? filter : null;
      }

      const boolFilter = BOOLEAN_FILTER_MAP[engagementFilter];
      if (boolFilter) {
        Object.assign(filter, boolFilter);
      } else if (ENUM_FILTER_MAP[engagementFilter]) {
        filter.recipientFilter = [ENUM_FILTER_MAP[engagementFilter]];
      } else {
        filter.recipientFilter = [engagementFilter.toUpperCase()];
      }
    }

    return Object.keys(filter).length > 0 ? filter : null;
  }, [stepEventId, timeframeFilter, engagementFilter]);

  const { members, totalCount, loading, error, refetch } = useCadenceMembers(cadenceId, {
    page: currentPage,
    pageSize: rowsPerPage,
    filter: serverFilter,
    searchQuery,
  });
  const { removeCadenceMember } = useCadenceMutations();

  // Refresh member list when the parent signals a data change (e.g. after add/remove/skip)
  const prevRefreshKeyRef = useRef(refreshKey);
  useEffect(() => {
    if (prevRefreshKeyRef.current === refreshKey) return;
    prevRefreshKeyRef.current = refreshKey;
    refetch();
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear selection when navigating to a different page
  useEffect(() => {
    setSelectedContactIds(new Set());
  }, [currentPage]);

  // Reset page when step filter changes
  useEffect(() => {
    setCurrentPage(0);
  }, [stepEventId]);

  const issueCount = members.filter((m) => (m.issues || []).length > 0).length;

  // The hook applies engagement/step/success filters server-side and search
  // across the full member list, returning the matched, enriched current page.
  // `totalCount` already reflects the active search.
  const effectiveTotalCount = totalCount;
  const totalPages = Math.max(1, Math.ceil(effectiveTotalCount / rowsPerPage));
  const paginatedMembers = members;

  const scrollToTable = () => {
    if (tableRef.current) {
      // Use 'nearest' to maintain scroll position — only scrolls if the
      // table header is out of view, preventing the page-jump issue.
      tableRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  const goToPage = (page) => {
    setCurrentPage(page);
    scrollToTable();
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(0);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(0);
  };

  const handleToggleSelect = (contactId) => {
    setSelectedContactIds((prev) => {
      const next = new Set(prev);
      if (next.has(contactId)) {
        next.delete(contactId);
      } else {
        next.add(contactId);
      }
      return next;
    });
  };

  const handleSelectAllOnPage = () => {
    const pageContactIds = paginatedMembers.map((m) => m.contactId).filter(Boolean);
    const allOnPageSelected = pageContactIds.length > 0 && pageContactIds.every((id) => selectedContactIds.has(id));

    setSelectedContactIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) {
        pageContactIds.forEach((id) => next.delete(id));
      } else {
        pageContactIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleBulkRemove = async () => {
    setBulkRemoving(true);
    const selectedMembers = members.filter((m) => m.contactId && selectedContactIds.has(m.contactId));
    let removed = 0;
    for (const member of selectedMembers) {
      try {
        await removeCadenceMember({
          cadenceId: String(cadenceId),
          memberId: String(member.id),
          removalReason: 'manual',
        });
        removed++;
      } catch (err) {
        // continue removing others
      }
    }
    if (removed > 0) {
      EverTrue.Alert.success(`Removed ${removed} recipient${removed !== 1 ? 's' : ''} from cadence`);
      refetch();
      onMembersChanged?.();
    } else {
      EverTrue.Alert.error('Failed to remove recipients');
    }
    setSelectedContactIds(new Set());
    setConfirmBulkRemove(false);
    setBulkRemoving(false);
  };

  const pageContactIds = paginatedMembers.map((m) => m.contactId).filter(Boolean);
  const allOnPageSelected = pageContactIds.length > 0 && pageContactIds.every((id) => selectedContactIds.has(id));
  const someOnPageSelected = pageContactIds.some((id) => selectedContactIds.has(id));

  if (loading) {
    return (
      <div className="cadence-recipients-table">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="cadence-recipients-table">
        <p className="cadence-recipients-table--error-message">Failed to load recipients: {error}</p>
      </div>
    );
  }

  return (
    <div className="cadence-recipients-table" ref={tableRef}>
      <div className="cadence-recipients-table--header">
        <div className="cadence-recipients-table--header-row">
          <div className="cadence-recipients-table--header-left">
            <h3 className="cadence-recipients-table--title">
              {titleProp ||
                (isBulk
                  ? 'Bulk Message Recipients'
                  : isStepView
                    ? `Step Recipients \u2014 ${selectedStep?.label || 'Step'}`
                    : 'Cadence Recipients')}
            </h3>
            <span className="cadence-recipients-table--count">
              {effectiveTotalCount} recipient
              {effectiveTotalCount !== 1 ? 's' : ''}
              {members.length === 0 && totalCount > 0 && !stepEventId && ' (member details unavailable)'}
            </span>
          </div>
          {(members.length > 0 || engagementFilter !== 'all' || searchQuery) && (
            <div className="cadence-recipients-table--controls">
              {!isBulk && issueCount > 0 && (
                <button
                  type="button"
                  className={`cadence-recipients-table--has-issues-chip ${hasIssuesFilter ? 'is-active' : ''}`}
                  onClick={() => {
                    setHasIssuesFilter((v) => !v);
                    setCurrentPage(0);
                  }}
                >
                  Has Issues ({issueCount})
                </button>
              )}
              <div className="cadence-recipients-table--search">
                <input
                  type="text"
                  placeholder="Search recipients..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
              </div>
              <select
                className="cadence-recipients-table--filter-select"
                value={engagementFilter}
                onChange={(e) => {
                  setEngagementFilter(e.target.value);
                  setCurrentPage(0);
                }}
              >
                {(isBulk
                  ? BULK_FILTERS
                  : isStepView
                    ? isManualStep
                      ? STEP_MANUAL_FILTERS
                      : STEP_EMAIL_FILTERS
                    : CADENCE_FILTERS
                ).map((f) => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
              <div className="cadence-recipients-table--manage-cols-wrap">
                <button
                  ref={colsBtnRef}
                  type="button"
                  className="cadence-recipients-table--manage-cols-btn"
                  onClick={() => setShowManageCols((v) => !v)}
                >
                  <Icon icon="settings" size={1} /> Columns
                </button>
                {showManageCols && (
                  <ManageRecipientColumnsPopover
                    columns={columnDefs}
                    visibility={colVisibility}
                    onChange={toggleColVisibility}
                    onClose={() => setShowManageCols(false)}
                    anchorRef={colsBtnRef}
                  />
                )}
              </div>
            </div>
          )}
        </div>
        {selectedContactIds.size > 0 && (
          <div className="cadence-recipients-table--selection-bar">
            <Checkbox
              label={`${selectedContactIds.size} selected`}
              checked={true}
              onChange={() => setSelectedContactIds(new Set())}
            >
              <span className="et--checkbox--label">{selectedContactIds.size} selected</span>
            </Checkbox>
            <div className="cadence-recipients-table--selection-actions">
              <Popover2
                trigger={({ togglePopover }) => (
                  <Button type="secondary" onClick={togglePopover}>
                    <Icon icon="add" /> Add to...
                  </Button>
                )}
              >
                {({ closePopover }) => (
                  <ContactAddTo
                    contactIds={Array.from(selectedContactIds)}
                    allSelected={false}
                    totalResultsCount={selectedContactIds.size}
                    closePopover={() => {
                      closePopover();
                      setSelectedContactIds(new Set());
                    }}
                  />
                )}
              </Popover2>
              {confirmBulkRemove ? (
                <>
                  <Button type="destroy" onClick={handleBulkRemove} disabled={bulkRemoving}>
                    {bulkRemoving ? 'Removing...' : `Yes, Remove ${selectedContactIds.size}`}
                  </Button>
                  <Button type="secondary" onClick={() => setConfirmBulkRemove(false)} disabled={bulkRemoving}>
                    Cancel
                  </Button>
                </>
              ) : (
                <Button type="destroy" onClick={() => setConfirmBulkRemove(true)}>
                  <Icon icon="cancel" /> Remove
                </Button>
              )}
            </div>
          </div>
        )}
      </div>

      {members.length === 0 && totalCount > 0 && (
        <p className="cadence-recipients-table--empty-message">
          {totalCount} member{totalCount !== 1 ? 's' : ''} enrolled. Individual member details are not yet available
          from the API.
        </p>
      )}

      {members.length === 0 && totalCount === 0 && (
        <p className="cadence-recipients-table--empty-message">
          {engagementFilter !== 'all' || searchQuery
            ? 'No recipients match the current filter.'
            : 'No recipients enrolled in this cadence yet.'}
        </p>
      )}

      {members.length > 0 && (
        <>
          <div className="cadence-recipients-table--table-wrapper">
            <table
              className={`cadence-recipients-table--table ${isBulk ? 'is-bulk' : isStepView ? 'is-step-view' : ''}`}
            >
              <thead>
                <tr>
                  <th className="cadence-recipients-table--checkbox-col col-checkbox">
                    <Checkbox
                      label="Select all"
                      checked={allOnPageSelected}
                      partial={someOnPageSelected && !allOnPageSelected}
                      onChange={handleSelectAllOnPage}
                    />
                  </th>
                  <th className="col-name">Name</th>
                  <th className="col-email">{isBulk ? 'Email / Phone' : 'Email'}</th>
                  {isBulk ? (
                    <>
                      {isColVisible('status') && <th className="col-status">Status</th>}
                      {isColVisible('sent') && <th className="cadence-recipients-table--check-cell col-check">Sent</th>}
                      {isColVisible('delivered') && (
                        <th className="cadence-recipients-table--check-cell col-check">Delivered</th>
                      )}
                      {isColVisible('opened') && (
                        <th className="cadence-recipients-table--check-cell col-check">Opened</th>
                      )}
                      {isColVisible('clicked') && (
                        <th className="cadence-recipients-table--check-cell col-check">Clicked</th>
                      )}
                      {isColVisible('replied') && (
                        <th className="cadence-recipients-table--check-cell col-check">Replied</th>
                      )}
                      {isColVisible('successReached') && (
                        <th className="cadence-recipients-table--check-cell col-check-success">Success Reached</th>
                      )}
                      {isColVisible('error') && (
                        <th className="cadence-recipients-table--check-cell col-check">Error</th>
                      )}
                      {isColVisible('unsubscribed') && (
                        <th className="cadence-recipients-table--check-cell col-check">Unsubscribed</th>
                      )}
                    </>
                  ) : isManualStep ? (
                    <>
                      {isColVisible('taskCompleted') && (
                        <th className="cadence-recipients-table--check-cell col-check">Task Completed</th>
                      )}
                      {isColVisible('taskSkipped') && (
                        <th className="cadence-recipients-table--check-cell col-check">Task Skipped</th>
                      )}
                      {isColVisible('successReached') && (
                        <th className="cadence-recipients-table--check-cell col-check-success">Success Reached</th>
                      )}
                      {isColVisible('exited') && (
                        <th className="cadence-recipients-table--check-cell col-check">Exited</th>
                      )}
                      <th className="col-action">View Details</th>
                    </>
                  ) : isStepView ? (
                    <>
                      {isColVisible('delivered') && (
                        <th className="cadence-recipients-table--check-cell col-check">Delivered</th>
                      )}
                      {isColVisible('opened') && (
                        <th className="cadence-recipients-table--check-cell col-check">Opened</th>
                      )}
                      {isColVisible('clicked') && (
                        <th className="cadence-recipients-table--check-cell col-check">Clicked</th>
                      )}
                      {isColVisible('replied') && (
                        <th className="cadence-recipients-table--check-cell col-check">Replied</th>
                      )}
                      {isColVisible('unsubscribed') && (
                        <th className="cadence-recipients-table--check-cell col-check">Unsubscribed</th>
                      )}
                      {isColVisible('exited') && (
                        <th className="cadence-recipients-table--check-cell col-check">Exited</th>
                      )}
                      <th className="col-action">View Details</th>
                    </>
                  ) : (
                    <>
                      {isColVisible('dateAdded') && <th className="col-date">Date Added</th>}
                      {isColVisible('status') && <th className="col-status">Current Status in Cadence</th>}
                      {isColVisible('reachedGoal') && (
                        <th className="cadence-recipients-table--check-cell col-check">Reached Goal</th>
                      )}
                      <th className="col-action">View Details</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {paginatedMembers.map((member) => {
                  const isSelected = member.contactId && selectedContactIds.has(member.contactId);

                  return (
                    <tr key={member.id} className={isSelected ? 'is-selected' : ''}>
                      <td className="cadence-recipients-table--checkbox-col col-checkbox">
                        {member.contactId && (
                          <Checkbox
                            label="Select member"
                            checked={isSelected}
                            onChange={() => handleToggleSelect(member.contactId)}
                          />
                        )}
                      </td>
                      <td className="cadence-recipients-table--name col-name">
                        <div className="cadence-recipients-table--name-row">
                          {member.contactImage ? (
                            <img src={member.contactImage} alt="" className="cadence-recipients-table--avatar" />
                          ) : (
                            <div className="cadence-recipients-table--avatar-placeholder">
                              <Icon icon="person" />
                            </div>
                          )}
                          {member.contactId ? (
                            <Link
                              href={`/contact/${member.contactId}`}
                              className="cadence-recipients-table--name-link"
                              title={`${member.contactName}'s Profile`}
                            >
                              {member.contactName}
                            </Link>
                          ) : (
                            <span>{member.contactName}</span>
                          )}
                        </div>
                      </td>
                      <td className="cadence-recipients-table--email-phone col-email">
                        <div className="cadence-recipients-table--email-line">
                          {member.contactEmail || (
                            <span className="cadence-recipients-table--missing-data">No email</span>
                          )}
                        </div>
                        {isBulk && (
                          <div className="cadence-recipients-table--phone-line">
                            {member.contactPhone || (
                              <span className="cadence-recipients-table--missing-data">No phone</span>
                            )}
                          </div>
                        )}
                      </td>
                      {isBulk ? (
                        <>
                          {isColVisible('status') && (
                            <td className="col-status">
                              <StatusBadge member={member} cadenceStatus={cadenceStatus} />
                            </td>
                          )}
                          {(() => {
                            const isDlv = member.displayStatus === 'delivered';
                            return (
                              <>
                                {isColVisible('sent') && (
                                  <td className="cadence-recipients-table--check-cell">
                                    {(member.totalEmailsSent ?? 0) > 0 || isDlv ? (
                                      <span className="cadence-recipients-table--check-yes">&#10003;</span>
                                    ) : (
                                      <span className="cadence-recipients-table--check-no">&#10005;</span>
                                    )}
                                  </td>
                                )}
                                {isColVisible('delivered') && (
                                  <td className="cadence-recipients-table--check-cell">
                                    {(member.totalEmailsDelivered ?? 0) > 0 || isDlv ? (
                                      <span className="cadence-recipients-table--check-yes">&#10003;</span>
                                    ) : (
                                      <span className="cadence-recipients-table--check-no">&#10005;</span>
                                    )}
                                  </td>
                                )}
                              </>
                            );
                          })()}
                          {(() => {
                            // Derive engagement from status + available fields (some fields not in query)
                            const wasOpened = (member.totalEmailsOpened ?? 0) > 0;
                            const wasClicked = (member.totalEmailsClicked ?? 0) > 0;
                            const wasReplied = (member.totalEmailsReplied ?? 0) > 0;
                            const hasError = member.displayStatus === 'failed_send';
                            return (
                              <>
                                {isColVisible('opened') && (
                                  <td className="cadence-recipients-table--check-cell">
                                    {wasOpened ? (
                                      <span className="cadence-recipients-table--check-yes">&#10003;</span>
                                    ) : (
                                      <span className="cadence-recipients-table--check-no">&#10005;</span>
                                    )}
                                  </td>
                                )}
                                {isColVisible('clicked') && (
                                  <td className="cadence-recipients-table--check-cell">
                                    {wasClicked ? (
                                      <span className="cadence-recipients-table--check-yes">&#10003;</span>
                                    ) : (
                                      <span className="cadence-recipients-table--check-no">&#10005;</span>
                                    )}
                                  </td>
                                )}
                                {isColVisible('replied') && (
                                  <td className="cadence-recipients-table--check-cell">
                                    {wasReplied ? (
                                      <span className="cadence-recipients-table--check-yes">&#10003;</span>
                                    ) : (
                                      <span className="cadence-recipients-table--check-no">&#10005;</span>
                                    )}
                                  </td>
                                )}
                                {isColVisible('successReached') && (
                                  <td className="cadence-recipients-table--check-cell">
                                    {member.isSuccessful ? (
                                      <span className="cadence-recipients-table--check-yes">&#10003;</span>
                                    ) : (
                                      <span className="cadence-recipients-table--check-no">&#10005;</span>
                                    )}
                                  </td>
                                )}
                                {isColVisible('error') && (
                                  <td className="cadence-recipients-table--check-cell">
                                    {hasError ? (
                                      <span className="cadence-recipients-table--check-yes">&#10003;</span>
                                    ) : (
                                      <span className="cadence-recipients-table--check-no">&#10005;</span>
                                    )}
                                  </td>
                                )}
                              </>
                            );
                          })()}
                          {isColVisible('unsubscribed') && (
                            <td className="cadence-recipients-table--check-cell">
                              {member.displayStatus === 'unsubscribed' ||
                              (member.issues || []).some(
                                (i) =>
                                  i.code === 'Unsubscribe' ||
                                  (i.message && i.message.toLowerCase().includes('unsubscribed'))
                              ) ? (
                                <span className="cadence-recipients-table--check-yes">&#10003;</span>
                              ) : (
                                <span className="cadence-recipients-table--check-no">&#10005;</span>
                              )}
                            </td>
                          )}
                        </>
                      ) : isManualStep ? (
                        <>
                          {isColVisible('taskCompleted') && (
                            <td className="cadence-recipients-table--check-cell">
                              {(() => {
                                const sr = stepRecipientMap?.[String(member.id)];
                                return sr?.taskCompleted ? (
                                  <span className="cadence-recipients-table--check-yes">&#10003;</span>
                                ) : (
                                  <span className="cadence-recipients-table--check-no">&#10005;</span>
                                );
                              })()}
                            </td>
                          )}
                          {isColVisible('taskSkipped') && (
                            <td className="cadence-recipients-table--check-cell">
                              {(() => {
                                const sr = stepRecipientMap?.[String(member.id)];
                                return sr?.taskSkipped ? (
                                  <span className="cadence-recipients-table--check-yes">&#10003;</span>
                                ) : (
                                  <span className="cadence-recipients-table--check-no">&#10005;</span>
                                );
                              })()}
                            </td>
                          )}
                          {isColVisible('successReached') && (
                            <td className="cadence-recipients-table--check-cell">
                              {(() => {
                                const sr = stepRecipientMap?.[String(member.id)];
                                return sr?.isSuccessful ? (
                                  <span className="cadence-recipients-table--check-yes">&#10003;</span>
                                ) : (
                                  <span className="cadence-recipients-table--check-no">&#10005;</span>
                                );
                              })()}
                            </td>
                          )}
                          {isColVisible('exited') && (
                            <td className="cadence-recipients-table--check-cell">
                              {stepRecipientMap?.[String(member.id)]?.exitedAtThisStep ? (
                                <span className="cadence-recipients-table--check-yes">&#10003;</span>
                              ) : (
                                <span className="cadence-recipients-table--check-no">&#10005;</span>
                              )}
                            </td>
                          )}
                          <td className="col-action">
                            <button
                              type="button"
                              className="cadence-recipients-table--view-link"
                              onClick={() => onViewDetails && onViewDetails(member)}
                            >
                              View Details
                            </button>
                          </td>
                        </>
                      ) : isStepView ? (
                        (() => {
                          // For step view, check stepRecipientMap activity + member aggregates
                          const sr = stepRecipientMap?.[String(member.id)];
                          const act = sr?.selectedStep || '';
                          // Use step-level activity when available, fall back to member-level totals
                          const DELIVERED_ACTS = new Set([
                            'email_delivered',
                            'email_opened',
                            'email_clicked',
                            'email_replied',
                          ]);
                          const OPENED_ACTS = new Set(['email_opened', 'email_clicked', 'email_replied']);
                          const wasDelivered =
                            DELIVERED_ACTS.has(act) ||
                            member.derivedDelivered === true ||
                            (member.totalEmailsDelivered ?? 0) > 0;
                          const wasOpened = OPENED_ACTS.has(act) || (member.totalEmailsOpened ?? 0) > 0;
                          const wasClicked = act === 'email_clicked' || (member.totalEmailsClicked ?? 0) > 0;
                          const wasReplied = act === 'email_replied' || (member.totalEmailsReplied ?? 0) > 0;
                          return (
                            <>
                              {isColVisible('delivered') && (
                                <td className="cadence-recipients-table--check-cell">
                                  {wasDelivered ? (
                                    <span className="cadence-recipients-table--check-yes">&#10003;</span>
                                  ) : (
                                    <span className="cadence-recipients-table--check-no">&#10005;</span>
                                  )}
                                </td>
                              )}
                              {isColVisible('opened') && (
                                <td className="cadence-recipients-table--check-cell">
                                  {wasOpened ? (
                                    <span className="cadence-recipients-table--check-yes">&#10003;</span>
                                  ) : (
                                    <span className="cadence-recipients-table--check-no">&#10005;</span>
                                  )}
                                </td>
                              )}
                              {isColVisible('clicked') && (
                                <td className="cadence-recipients-table--check-cell">
                                  {wasClicked ? (
                                    <span className="cadence-recipients-table--check-yes">&#10003;</span>
                                  ) : (
                                    <span className="cadence-recipients-table--check-no">&#10005;</span>
                                  )}
                                </td>
                              )}
                              {isColVisible('replied') && (
                                <td className="cadence-recipients-table--check-cell">
                                  {wasReplied ? (
                                    <span className="cadence-recipients-table--check-yes">&#10003;</span>
                                  ) : (
                                    <span className="cadence-recipients-table--check-no">&#10005;</span>
                                  )}
                                </td>
                              )}
                              {isColVisible('unsubscribed') && (
                                <td className="cadence-recipients-table--check-cell">
                                  {member.displayStatus === 'unsubscribed' ? (
                                    <span className="cadence-recipients-table--check-yes">&#10003;</span>
                                  ) : (
                                    <span className="cadence-recipients-table--check-no">&#10005;</span>
                                  )}
                                </td>
                              )}
                              {isColVisible('exited') && (
                                <td className="cadence-recipients-table--check-cell">
                                  {sr?.exitedAtThisStep ? (
                                    <span className="cadence-recipients-table--check-yes">&#10003;</span>
                                  ) : (
                                    <span className="cadence-recipients-table--check-no">&#10005;</span>
                                  )}
                                </td>
                              )}
                              <td className="col-action">
                                <button
                                  type="button"
                                  className="cadence-recipients-table--view-link"
                                  onClick={() => onViewDetails && onViewDetails(member)}
                                >
                                  View Details
                                </button>
                              </td>
                            </>
                          );
                        })()
                      ) : (
                        <>
                          {isColVisible('dateAdded') && (
                            <td className="col-date">{formatDateOrDash(member.enrolledAt)}</td>
                          )}
                          {isColVisible('status') && (
                            <td className="col-status">
                              <div className="cadence-recipients-table--status-cell">
                                <StatusBadge member={member} cadenceStatus={cadenceStatus} />
                                {member.nextEventScheduledAt &&
                                  (member.displayStatus === 'in_progress' || member.displayStatus === 'enrolled') && (
                                    <WaitCountdown targetDate={member.nextEventScheduledAt} compact />
                                  )}
                              </div>
                            </td>
                          )}
                          {isColVisible('reachedGoal') && (
                            <td className="cadence-recipients-table--check-cell">
                              {member.isSuccessful ? (
                                <span className="cadence-recipients-table--check-yes">&#10003;</span>
                              ) : (
                                <span className="cadence-recipients-table--check-no">&#10005;</span>
                              )}
                            </td>
                          )}
                          <td className="col-action">
                            <button
                              type="button"
                              className="cadence-recipients-table--view-link"
                              onClick={() => onViewDetails && onViewDetails(member)}
                            >
                              View Details
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Status legend for bulk messages */}
          {isBulk && (
            <div className="cadence-recipients-table--status-legend">
              <span className="cadence-recipients-table--status-legend-label">STATUS LEGEND:</span>
              {BULK_FILTERS.filter((f) => f.value !== 'all').map((f) => {
                const style = STATUS_MAP[f.value] || {};
                return (
                  <span key={f.value} className="cadence-recipients-table--status-legend-item">
                    <span
                      className="cadence-recipients-table--status-legend-dot"
                      style={{ background: style.color || '#6b7280' }}
                    />
                    {f.label}
                  </span>
                );
              })}
              <span className="cadence-recipients-table--status-legend-hint">
                Hover over Skipped/Failed badges for details. Hover over Success Reached for metric info.
              </span>
            </div>
          )}

          {totalCount > 0 && (
            <div className="cadence-recipients-table--pagination">
              <div className="cadence-recipients-table--pagination-left">
                <span>Rows per page:</span>
                <select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
                <span className="cadence-recipients-table--pagination-info">
                  {currentPage * rowsPerPage + 1}-{Math.min((currentPage + 1) * rowsPerPage, totalCount)} of{' '}
                  {totalCount}
                </span>
              </div>
              <div className="cadence-recipients-table--pagination-right">
                <button
                  type="button"
                  className="cadence-recipients-table--page-btn"
                  disabled={currentPage === 0}
                  onClick={() => goToPage(currentPage - 1)}
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum = i;
                  if (totalPages > 5 && currentPage > 2) {
                    pageNum = Math.min(currentPage - 2 + i, totalPages - 1);
                  }
                  if (pageNum >= totalPages) return null;
                  return (
                    <button
                      key={pageNum}
                      type="button"
                      className={`cadence-recipients-table--page-btn ${currentPage === pageNum ? 'is-active' : ''}`}
                      onClick={() => goToPage(pageNum)}
                    >
                      {pageNum + 1}
                    </button>
                  );
                })}
                <button
                  type="button"
                  className="cadence-recipients-table--page-btn"
                  disabled={currentPage >= totalPages - 1}
                  onClick={() => goToPage(currentPage + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

CadenceRecipientsTable.propTypes = {
  cadenceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  cadenceStatus: PropTypes.string,
  onViewDetails: PropTypes.func,
  stepEventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  stepMemberIds: PropTypes.instanceOf(Set),
  stepRecipientMap: PropTypes.object,
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
      type: PropTypes.string,
    })
  ),
  timeframeFilter: PropTypes.string,
  variant: PropTypes.string,
  title: PropTypes.string,
  refreshKey: PropTypes.number,
  onMembersChanged: PropTypes.func,
};

export default CadenceRecipientsTable;
